import {
  ColumnDef,
  getCoreRowModel,
  SortingState,
  useReactTable,
  ColumnFiltersState,
  PaginationState,
  OnChangeFn,
  RowSelectionState,
} from '@tanstack/react-table';
import { Spinner, Table, Button } from '@platform/components';
import { columnFiltersToObject, formatFilter, formatSortState } from '../util/FormatFilterState';
import { toSnakeCase } from '../util/SnakeCase';
import { useContext, createContext, useState } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { CSVLink } from 'react-csv';
import { ModalCallToAction } from '@platform/components';

export const getAdminQueryOptions = (
  sorting?: SortingState,
  columnFilters?: ColumnFiltersState,
  pagination?: PaginationState,
) => {
  const queryOptions: {
    page: number;
    limit: number;
    sort?: string;
    filter?: string;
  } = { page: 0, limit: 20 };

  if (sorting) {
    queryOptions.sort = sorting
      .map((s) => `${toSnakeCase(s.id)}:${s.desc ? 'DESC' : 'ASC'}`)
      .join(',');
  }

  if (columnFilters) {
    queryOptions.filter = formatFilter(columnFilters);
  }

  if (pagination) {
    queryOptions.page = pagination.pageIndex;
    queryOptions.limit = pagination.pageSize;
  }

  return queryOptions;
};

export type AdminQueryOptions = {
  page: number;
  limit: number;
  sort?: string;
  filter?: string;
};

/** A temporary type to replace AdminQueryOptions if proposed implementation accepted */
export type AdminQueryOptionsV2 = Record<string, string | number> & {
  page?: number;
  limit?: number;
  sort?: string;
};

export const AdminTableContext = createContext<{
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  columnFilters: ColumnFiltersState;
  setColumnFilters: OnChangeFn<ColumnFiltersState>;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  adminQueryOptions: AdminQueryOptions;
  adminQueryOptionsV2: AdminQueryOptionsV2;
  filteredCount: number;
  setFilteredCount: OnChangeFn<number>;
  downloadData?: object[];
  setDownloadData?: (data: object[]) => void;
}>({
  sorting: [],
  setSorting: () => {},
  columnFilters: [],
  setColumnFilters: () => {},
  pagination: { pageIndex: 0, pageSize: 20 },
  setPagination: () => {},
  adminQueryOptions: { page: 0, limit: 20 },
  adminQueryOptionsV2: {},
  rowSelection: {},
  setRowSelection: () => {},
  filteredCount: 0,
  setFilteredCount: () => {},
});

export const AdminTableProvider = ({ children }: { children: React.ReactNode }) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [filteredCount, setFilteredCount] = useState(0);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const adminQueryOptions = getAdminQueryOptions(sorting, columnFilters, pagination);
  const adminQueryOptionsV2 = {
    page: pagination.pageIndex,
    limit: pagination.pageSize,
    sort: formatSortState(sorting),
    ...columnFiltersToObject(columnFilters),
  };

  return (
    <AdminTableContext.Provider
      value={{
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        pagination,
        setPagination,
        adminQueryOptions,
        adminQueryOptionsV2,
        rowSelection,
        setRowSelection,
        filteredCount,
        setFilteredCount,
      }}
    >
      {children}
    </AdminTableContext.Provider>
  );
};

type AdminTableExportProps<TExport> = {
  exportQuery: (options: Record<string, string>) => UseQueryResult<TExport>;
  dataToExportFn: (data: TExport) => object[];
  exportFileName?: string;
};

interface AdminTableProps<TData, TExport> {
  data: TData[];
  filteredCount: number;
  columns: ColumnDef<TData>[];
  getRowId?: (row: TData) => string;
  isLoading?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  enableFiltering?: boolean;
  enableSorting?: boolean;
  enablePagination?: boolean;
  enableMultiRowSelection?: boolean;
  tableActions?: ModalCallToAction[];
  exportOptions?: AdminTableExportProps<TExport>;
}

// I am reaching a bit here on my knowledge of generics, the TExport = unknown is my attempt to make this optional?
export const AdminTable = <T, TExport = unknown>(props: AdminTableProps<T, TExport>) => {
  const {
    data,
    filteredCount,
    columns,
    getRowId,
    isLoading = true,
    isError = false,
    isSuccess = false,
    enableFiltering = false,
    enableSorting = true,
    enablePagination = true,
    enableMultiRowSelection = false,
    exportOptions,
  } = props;
  const {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    pagination,
    setPagination,
    rowSelection,
    setRowSelection,
  } = useContext(AdminTableContext);

  const adminTable = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    manualPagination: enablePagination,
    onPaginationChange: setPagination,
    rowCount: filteredCount,
    state: {
      pagination,
      sorting,
      columnFilters,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
      },
    },
    manualSorting: enableSorting,
    onSortingChange: setSorting,
    manualFiltering: enableFiltering,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      if (setRowSelection) {
        setRowSelection({}); // on any filter change we reset row selection to prevent updates to records filtered out of view
      }
    },
    enableMultiRowSelection,
    onRowSelectionChange: setRowSelection,
    getRowId: getRowId,
  });

  return (
    <>
      {isLoading && (
        <div className='flex justify-center'>
          <Spinner />
        </div>
      )}
      {isError && (
        <div className='flex justify-center'>
          <p className='text-red-500'>Error loading data</p>
        </div>
      )}
      {isSuccess && (
        <>
          <TableHeader
            filteredCount={filteredCount}
            tableActions={props.tableActions}
            exportOptions={(exportOptions as AdminTableExportProps<TExport>) ?? undefined}
          />
          <Table table={adminTable} />
        </>
      )}
    </>
  );
};

const TableHeader = <TExport,>(props: {
  filteredCount: number;
  tableActions?: ModalCallToAction[];
  exportOptions?: AdminTableExportProps<TExport>;
}) => {
  const { pagination, rowSelection, columnFilters, sorting } = useContext(AdminTableContext);
  const { filteredCount } = props;
  const startCount = Math.min(pagination.pageIndex * pagination.pageSize + 1, filteredCount);
  const endCount = Math.min((pagination.pageIndex + 1) * pagination.pageSize, filteredCount);
  const selectedCount = Object.keys(rowSelection ?? {}).length;
  return (
    <div className='flex justify-between items-end'>
      <div className='text-sm'>
        Viewing {startCount} - {endCount} of {filteredCount} total records{' '}
        {selectedCount > 0 && (
          <span className='text-xs font-semibold'> - ({selectedCount} selected)</span>
        )}
      </div>
      <div className='flex gap-2'>
        {props.exportOptions && (
          <ExportCSVButton
            sort={sorting}
            filter={columnFilters}
            exportQuery={props.exportOptions.exportQuery}
            dataToExportFn={props.exportOptions.dataToExportFn}
            isDisabled={!columnFilters}
          />
        )}
        {props.tableActions && (
          <div className='flex gap-2'>
            {props.tableActions.map((action, index) => (
              <div key={index} className='flex items-center'>
                <Button
                  size='sm'
                  onPress={action.callback}
                  variant={action.buttonVariant ?? 'secondary-color'}
                  isDisabled={action.isDisabled?.()}
                >
                  {action.text}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface DownloadCSVButtonProps<TExport> {
  sort: SortingState;
  filter: ColumnFiltersState;
  exportQuery: (options: Record<string, string>) => UseQueryResult<TExport>;
  dataToExportFn: (data: TExport) => object[];
}

const DownloadCSVButton = <TExport,>(props: DownloadCSVButtonProps<TExport>) => {
  const { data, isFetching, isFetched } = props.exportQuery({
    ...columnFiltersToObject(props.filter),
    sort: formatSortState(props.sort),
  });
  return (
    <Button
      variant='primary'
      size='sm'
      additionalClassnames='w-fit ml-auto'
      isDisabled={!isFetched}
    >
      {isFetching && 'Creating csv'}
      {isFetched && data && (
        <CSVLink
          filename={`export_${new Date().toISOString().replace(' ', '_')}.csv`}
          data={props.dataToExportFn(data)}
        >
          Download csv
        </CSVLink>
      )}
    </Button>
  );
};

const ExportCSVButton = <TExport,>(props: {
  sort: SortingState;
  filter: ColumnFiltersState;
  isDisabled?: boolean;
  exportQuery: (options: Record<string, string>) => UseQueryResult<TExport>;
  dataToExportFn: (data: TExport) => object[];
}) => {
  const [buttonStage, setButtonStage] = useState<'export' | 'download'>('export');

  return buttonStage === 'export' ?
      <Button
        variant='secondary-color'
        size='sm'
        onPress={() => {
          setButtonStage('download');
        }}
        additionalClassnames='w-fit'
        isDisabled={!props.filter || props.filter.length === 0}
      >
        Export to csv
      </Button>
    : <DownloadCSVButton<TExport>
        sort={props.sort}
        filter={props.filter}
        exportQuery={props.exportQuery}
        dataToExportFn={props.dataToExportFn}
      />;
};
